"use client";
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

export default function CommentsDrawer({ articleId, publicationId, currentPrefix }) {
  const [isOpen, setIsOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [sort, setSort] = useState('desc');
  const [search, setSearch] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [commentBody, setCommentBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  
  const pathname = usePathname();
  const loginUrl = `${currentPrefix ? '/' + currentPrefix : ''}/reader/login?returnTo=${encodeURIComponent(pathname)}`;
  const registerUrl = `${currentPrefix ? '/' + currentPrefix : ''}/reader/register?returnTo=${encodeURIComponent(pathname)}`;

  useEffect(() => {
    if (window.location.hash === '#comments') {
      setIsOpen(true);
    }
    
    // Fetch session
    fetch('/api/reader/auth/session')
      .then(res => res.json())
      .then(data => {
        if (data.reader) setSession(data.reader);
      })
      .catch(() => {});

    // Fetch comments
    fetchComments();
    const handleHashChange = () => setIsOpen(window.location.hash === '#comments');
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      document.body.style.overflow = 'auto';
    };
  }, [articleId, publicationId, sort]);

  const fetchComments = async () => {
    try {
      const res = await fetch(`/api/comments?articleId=${articleId}&publicationId=${publicationId}&sort=${sort}`);
      const data = await res.json();
      if (data.comments) {
        setComments(data.comments);
        setCommentCount(data.count ?? data.comments.length);
      }
    } catch {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    window.history.pushState(null, null, '#comments');
    document.body.style.overflow = 'hidden';
  };

  const handleClose = () => {
    setIsOpen(false);
    window.history.pushState(null, null, pathname);
    document.body.style.overflow = 'auto';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commentBody.trim()) return;
    setSubmitting(true);
    setSubmitMessage('');

    try {
      const res = await fetch('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ articleId, publicationId, body: commentBody })
      });
      const data = await res.json();
      
      if (res.ok) {
        setSubmitMessage(data.message);
        setCommentBody('');
        fetchComments();
      } else {
        setSubmitMessage(`Error: ${data.error}`);
      }
    } catch (err) {
      setSubmitMessage('Error submitting comment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim() || !replyingTo) return;
    setSubmitting(true);
    const res = await fetch('/api/comments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ articleId, publicationId, parentId: replyingTo, body: replyBody })
    });
    const data = await res.json();
    setSubmitMessage(res.ok ? data.message : `Error: ${data.error}`);
    if (res.ok) {
      setReplyBody('');
      setReplyingTo(null);
      await fetchComments();
    }
    setSubmitting(false);
  };

  const handleLike = async (commentId) => {
    if (!session) return;
    const res = await fetch('/api/comments/like', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ commentId })
    });
    if (res.ok) await fetchComments();
  };

  const handleReport = async (commentId) => {
    if (!session) return;
    const reason = window.prompt('Why are you reporting this comment?');
    if (!reason?.trim()) return;
    const res = await fetch('/api/comments/report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ commentId, reason: reason.trim() })
    });
    const data = await res.json();
    setSubmitMessage(res.ok ? data.message : `Error: ${data.error}`);
  };

  const visibleComments = comments.filter(comment => {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return comment.body.toLowerCase().includes(needle)
      || comment.reader?.displayName?.toLowerCase().includes(needle)
      || comment.replies?.some(reply => reply.body.toLowerCase().includes(needle));
  });

  return (
    <div className="w-full max-w-[620px] mx-auto mt-8 pt-6 border-t border-gray-200 flex justify-center">
      <button onClick={handleOpen} className="w-full max-w-[620px] mx-auto block bg-gray-800 hover:bg-black text-white font-sans font-bold text-sm py-4 px-6 rounded shadow-sm tracking-widest uppercase transition-colors">
        READ {commentCount} {commentCount === 1 ? 'COMMENT' : 'COMMENTS'}
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/20" onClick={handleClose}></div>
          
          {/* Drawer */}
          <div className="relative w-full md:w-[480px] lg:w-[560px] h-full bg-white border-l border-gray-300 shadow-xl flex flex-col transform transition-transform">
            
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="font-classic font-bold text-xl uppercase tracking-widest">
                {commentCount} Comments
              </h2>
              <button onClick={handleClose} className="p-2 text-2xl leading-none">&times;</button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 font-sans">
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <label className="sr-only" htmlFor={`comment-search-${articleId}`}>Search comments</label>
                <input id={`comment-search-${articleId}`} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search comments" className="flex-1 border border-gray-300 px-3 py-2 text-sm" />
                <select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="Sort comments" className="border border-gray-300 px-3 py-2 text-sm bg-white">
                  <option value="desc">Newest</option>
                  <option value="asc">Oldest</option>
                </select>
              </div>
              
              {!session && (
                <div className="bg-gray-50 p-6 border border-gray-200 text-center mb-8">
                  <h3 className="font-classic font-bold text-xl mb-2">Join the conversation</h3>
                  <p className="text-sm text-gray-600 mb-4">Sign in to comment or create a free reader account.</p>
                  <div className="flex justify-center space-x-4">
                    <Link href={loginUrl} className="bg-black text-white px-4 py-2 uppercase text-xs font-bold hover:bg-gray-800">Sign In</Link>
                    <Link href={registerUrl} className="border border-black px-4 py-2 uppercase text-xs font-bold hover:bg-gray-100">Create Account</Link>
                  </div>
                </div>
              )}

              {session && session.status !== 'ACTIVE' && (
                <div className="bg-yellow-50 p-4 border border-yellow-200 text-center mb-8">
                  <p className="text-sm text-yellow-800 font-medium">Verify your email to join the conversation.</p>
                </div>
              )}

              {session && session.status === 'ACTIVE' && (
                <div className="mb-8 border border-gray-200 p-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center font-bold text-gray-600">
                      {session.displayName.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium text-sm">{session.displayName}</span>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <textarea 
                      value={commentBody}
                      onChange={(e) => setCommentBody(e.target.value)}
                      maxLength={2000}
                      className="w-full border border-gray-300 p-3 text-sm min-h-[100px] focus:outline-none focus:border-black resize-y"
                      placeholder="What are your thoughts?"
                      required
                    ></textarea>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-gray-500">{commentBody.length}/2000</span>
                      <button 
                        type="submit" 
                        disabled={submitting || !commentBody.trim()}
                        className="bg-black text-white px-6 py-2 uppercase text-xs font-bold hover:bg-gray-800 disabled:bg-gray-400"
                      >
                        {submitting ? 'Posting...' : 'Post Comment'}
                      </button>
                    </div>
                  </form>
                  {submitMessage && (
                    <div className="mt-4 p-3 bg-blue-50 text-blue-800 text-sm font-medium border border-blue-200">
                      {submitMessage}
                    </div>
                  )}
                </div>
              )}

              {/* Comments List */}
              <div className="space-y-6">
                {loading ? (
                  <p className="text-sm text-gray-500">Loading comments...</p>
                ) : comments.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No comments yet. Be the first to share your thoughts.</p>
                ) : (
                  visibleComments.map(comment => (
                    <div key={comment.id} className="border-b border-gray-100 pb-6">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                          {comment.reader ? comment.reader.displayName.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <span className="font-medium text-sm block">
                            {comment.reader ? comment.reader.displayName : 'Deleted User'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{comment.body}</p>
                      
                      <div className="mt-3 flex space-x-4 text-xs font-medium text-gray-500">
                        {session?.status === 'ACTIVE' && <button onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)} className="hover:text-black uppercase">Reply</button>}
                        <button onClick={() => handleLike(comment.id)} disabled={!session} className="hover:text-black uppercase disabled:opacity-40">Like {comment.likeCount > 0 && `(${comment.likeCount})`}</button>
                        <button onClick={() => handleReport(comment.id)} disabled={!session} className="hover:text-black uppercase disabled:opacity-40">Report</button>
                      </div>

                      {replyingTo === comment.id && (
                        <form onSubmit={handleReply} className="mt-4">
                          <textarea value={replyBody} onChange={(event) => setReplyBody(event.target.value)} maxLength={2000} required className="w-full border border-gray-300 p-3 text-sm min-h-[80px]" placeholder="Write a reply" />
                          <div className="flex justify-end gap-2 mt-2">
                            <button type="button" onClick={() => setReplyingTo(null)} className="px-3 py-2 text-xs font-bold uppercase">Cancel</button>
                            <button type="submit" disabled={submitting || !replyBody.trim()} className="bg-black text-white px-4 py-2 text-xs font-bold uppercase disabled:opacity-40">Reply</button>
                          </div>
                        </form>
                      )}

                      {/* Replies */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="mt-4 pl-8 border-l-2 border-gray-100 space-y-4">
                          {comment.replies.map(reply => (
                            <div key={reply.id}>
                              <div className="flex items-center space-x-3 mb-1">
                                <span className="font-medium text-sm">
                                  {reply.reader ? reply.reader.displayName : 'Deleted User'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(reply.createdAt).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap">{reply.body}</p>
                              <div className="mt-2 flex space-x-4 text-xs font-medium text-gray-500">
                                <button onClick={() => handleLike(reply.id)} disabled={!session} className="hover:text-black uppercase disabled:opacity-40">Like {reply.likeCount > 0 && `(${reply.likeCount})`}</button>
                                <button onClick={() => handleReport(reply.id)} disabled={!session} className="hover:text-black uppercase disabled:opacity-40">Report</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}




